import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, ArrowRight, Loader2, XCircle, Download, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegistrationSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [registrationData, setRegistrationData] = useState(null);
  const [error, setError] = useState('');
  const [showCodesModal, setShowCodesModal] = useState(false);
  const codesRef = useRef(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const isFree = searchParams.get('free') === 'true';
    
    if (isFree) {
      // Free registration via access code — codes are already in query params, no Stripe confirm needed.
      const pid = searchParams.get('participant_id');
      if (!pid) {
        setError('Missing registration details');
        setLoading(false);
        return;
      }
      setPaymentStatus('paid');
      setRegistrationData({
        participant_id: pid,
        event_code: searchParams.get('event_code'),
        parent_access_code: searchParams.get('parent_access_code'),
        child_name: searchParams.get('child_name'),
        parent_email: searchParams.get('parent_email'),
        child: { full_name: searchParams.get('child_name') },
        username: searchParams.get('username') || '',
      });
      setShowCodesModal(true);
      setLoading(false);
      return;
    }
    
    if (sessionId) {
      confirmPayment(sessionId);
    } else {
      setError('No payment session found');
      setLoading(false);
    }
  }, [searchParams]);

  const confirmPayment = async (sessionId) => {
    try {
      const response = await axios.post(`${API_URL}/api/payments/confirm/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('paid');
        setRegistrationData(response.data.registration);
        setShowCodesModal(true);
        setLoading(false);
      } else {
        // Payment not yet confirmed — retry a few times
        pollForConfirmation(sessionId, 0);
      }
    } catch (err) {
      console.error('Confirm error:', err);
      setError('Error verifying payment. Please contact support.');
      setLoading(false);
    }
  };

  // How long we keep polling Stripe for "paid" before falling back to the
  // "we've emailed you" reassurance screen. Live Stripe (esp. with 3DS / SCA)
  // can take 30+ seconds to flip a session from "unpaid" → "paid", so the
  // window must be generous. Webhook is the eventual source of truth — this
  // poll is purely for nice UX on the success page.
  const POLL_MAX_ATTEMPTS = 45;   // 45 × 2s = 90s
  const POLL_INTERVAL_MS  = 2000;
  const [pollProgress, setPollProgress] = useState(0);

  const pollForConfirmation = async (sessionId, attempts) => {
    if (attempts >= POLL_MAX_ATTEMPTS) {
      // Soft fallback: payment was almost certainly captured by Stripe
      // (otherwise Stripe wouldn't have redirected here) — we just couldn't
      // verify within the polling window. Don't scare the parent; point them
      // at the confirmation email instead.
      setPaymentStatus('pending_email');
      setLoading(false);
      return;
    }
    setPollProgress(Math.min(100, Math.round((attempts / POLL_MAX_ATTEMPTS) * 100)));
    setTimeout(async () => {
      try {
        const response = await axios.post(`${API_URL}/api/payments/confirm/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          setPaymentStatus('paid');
          setRegistrationData(response.data.registration);
          setShowCodesModal(true);
          setLoading(false);
        } else {
          pollForConfirmation(sessionId, attempts + 1);
        }
      } catch {
        pollForConfirmation(sessionId, attempts + 1);
      }
    }, POLL_INTERVAL_MS);
  };

  const downloadCodes = () => {
    if (!registrationData) return;
    const content = `
========================================
   BRAIN GYM JR OLYMPIAD
   Registration Confirmation
========================================

Child Name:        ${registrationData.child?.full_name}
Username:          ${registrationData.username}
Participant ID:    ${registrationData.participant_id}

----------------------------------------
   ACCESS CODES (KEEP THESE SAFE)
----------------------------------------

Event Code:        ${registrationData.event_code}
Parent Access Code: ${registrationData.parent_access_code}

----------------------------------------

Event Code: Your child uses this to join
the competition on event day.

Parent Access Code: Use this to check
your child's results and progress.

========================================
    `.trim();

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `BrainGym_Registration_${registrationData.participant_id}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    const hint = pollProgress > 25
      ? "Stripe is taking a few extra seconds — this is normal for some cards. Please don't close this tab."
      : 'Please wait while we confirm your payment';
    return (
      <div className="min-h-screen bg-[#fdfdfc] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <Loader2 className="w-16 h-16 text-[#2565eb] animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#0f172a] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Verifying Payment...
          </h2>
          <p className="text-[#64748b]">{hint}</p>
          {pollProgress > 0 && (
            <div className="mt-5 w-64 mx-auto h-1.5 bg-[#e2e8f0] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#2565eb] transition-all duration-500"
                style={{ width: `${pollProgress}%` }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Soft fallback after the long poll window expired without seeing "paid".
  // Stripe almost certainly took the payment (they redirected us here) and
  // the webhook / email path will finish the job — we just couldn't observe
  // the flip in time. Reassure the parent, don't show a scary error.
  if (paymentStatus === 'pending_email') {
    return (
      <div className="min-h-screen bg-[#fdfdfc] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-[#facc15] rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-[0_6px_0_#ca8a04]">
            <CheckCircle className="w-14 h-14 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-[#0f172a] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Payment Received
          </h2>
          <p className="text-[#475569] mb-2">
            Thank you! Your payment went through, but our system is still
            confirming the details with Stripe.
          </p>
          <p className="text-[#475569] mb-6">
            <strong>You will receive a confirmation email within a few minutes</strong> with
            your child&apos;s login credentials and parent access code.
          </p>
          <p className="text-xs text-[#94a3b8] mb-6">
            If you don&apos;t see the email within 10 minutes, please check your spam
            folder or contact support.
          </p>
          <button
            onClick={() => navigate('/parent')}
            className="btn-secondary"
            data-testid="pending-email-parent-portal-btn"
          >
            Go to Parent Portal
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#fdfdfc] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-[#ef4444] rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-[0_6px_0_#b91c1c]">
            <XCircle className="w-14 h-14 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Payment Issue
          </h2>
          <p className="text-[#64748b] mb-6">{error}</p>
          <button
            onClick={() => navigate('/registration')}
            className="btn-secondary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fdfdfc] flex flex-col">
      {/* Header */}
      <header className="p-6 flex items-center justify-center border-b border-[#e2e8f0]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden">
            <img src="/logo.png" alt="Brain Gym" className="w-10 h-10 object-contain" />
          </div>
          <span className="text-xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Brain Gym
          </span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="text-center max-w-lg animate-fade-in-up">
          <div className="w-24 h-24 bg-[#22c55e] rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-[0_6px_0_#16a34a]">
            <CheckCircle className="w-14 h-14 text-white" />
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Payment Successful!
          </h1>
          
          <p className="text-[#64748b] text-lg mb-8">
            Your registration is now complete.
          </p>

          {registrationData && (
            <div className="card-olympiad p-6 mb-8 text-left">
              <h3 className="font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Registration Details
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-[#e2e8f0]">
                  <span className="text-sm text-[#64748b]">Child Name</span>
                  <span className="font-bold text-[#0f172a]">{registrationData.child?.full_name}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[#e2e8f0]">
                  <span className="text-sm text-[#64748b]">Username</span>
                  <span className="font-bold text-[#2565eb]">{registrationData.username}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[#e2e8f0]">
                  <span className="text-sm text-[#64748b]">Participant ID</span>
                  <span className="font-mono font-bold text-[#0f172a]">{registrationData.participant_id}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[#e2e8f0]">
                  <span className="text-sm text-[#64748b]">Event Code</span>
                  <span className="font-mono font-bold text-[#16a34a] bg-[#dcfce7] px-3 py-1 rounded-lg">{registrationData.event_code}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-[#64748b]">Parent Access Code</span>
                  <span className="font-mono font-bold text-[#2565eb] bg-[#eff6ff] px-3 py-1 rounded-lg">{registrationData.parent_access_code}</span>
                </div>
              </div>
            </div>
          )}

          <div className="card-olympiad p-6 mb-8 text-left">
            <h3 className="font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              What's Next?
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#2565eb] text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">1</div>
                <span className="text-[#475569]">Check your email for the confirmation with your access codes</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#2565eb] text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">2</div>
                <span className="text-[#475569]">Access the Practice Zone to prepare for the competition</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#2565eb] text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">3</div>
                <span className="text-[#475569]">Wait for the host to activate the event, then use your Event Code to join</span>
              </li>
            </ul>
          </div>

          <div className="p-4 bg-[#fef3c7] rounded-2xl border-2 border-[#f59e0b] mb-8">
            <p className="text-sm text-[#92400e]">
              <strong>Important:</strong> Your event code and parent access code will activate when the host starts the event. Your practice zone is available immediately.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/practice')}
              className="btn-primary flex items-center justify-center gap-2"
              data-testid="go-to-practice-button"
            >
              Go to Practice Zone
              <ArrowRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate('/')}
              className="btn-secondary flex items-center justify-center gap-2"
              data-testid="back-to-home-button"
            >
              Back to Home
            </button>
          </div>
        </div>
      </main>

      {/* Codes Modal */}
      {showCodesModal && registrationData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="codes-modal-overlay">
          <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl animate-fade-in-up overflow-hidden" data-testid="codes-modal" ref={codesRef}>
            {/* Header */}
            <div className="bg-[#22c55e] px-6 py-5 text-center">
              <CheckCircle className="w-12 h-12 text-white mx-auto mb-2" />
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Registration Successful!
              </h2>
              <p className="text-sm text-[#dcfce7] mt-1">Save these codes — you'll need them on event day</p>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              <div className="space-y-3 mb-5">
                <div className="flex justify-between items-center py-2 border-b border-[#e2e8f0]">
                  <span className="text-sm text-[#64748b]">Child</span>
                  <span className="font-bold text-[#0f172a]">{registrationData.child?.full_name}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[#e2e8f0]">
                  <span className="text-sm text-[#64748b]">Username</span>
                  <span className="font-bold text-[#2565eb]">{registrationData.username}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[#e2e8f0]">
                  <span className="text-sm text-[#64748b]">Participant ID</span>
                  <span className="font-mono font-bold text-[#0f172a]">{registrationData.participant_id}</span>
                </div>
              </div>

              {/* Codes — prominent */}
              <div className="space-y-3 mb-5">
                <div className="bg-[#dcfce7] border-2 border-[#22c55e] rounded-2xl p-4 text-center">
                  <p className="text-xs font-bold text-[#16a34a] uppercase tracking-wide mb-1">Event Code</p>
                  <p className="text-2xl font-mono font-bold text-[#15803d] tracking-widest" data-testid="modal-event-code">{registrationData.event_code}</p>
                  <p className="text-xs text-[#16a34a] mt-1">Your child uses this to join the competition</p>
                </div>
                <div className="bg-[#eff6ff] border-2 border-[#2565eb] rounded-2xl p-4 text-center">
                  <p className="text-xs font-bold text-[#2565eb] uppercase tracking-wide mb-1">Parent Access Code</p>
                  <p className="text-2xl font-mono font-bold text-[#1e3a8a] tracking-widest" data-testid="modal-parent-code">{registrationData.parent_access_code}</p>
                  <p className="text-xs text-[#2565eb] mt-1">Use this to check your child's results</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={downloadCodes}
                  className="w-full px-5 py-3 rounded-xl font-bold bg-[#0f172a] text-white text-sm flex items-center justify-center gap-2 hover:bg-[#1e293b] transition-all"
                  data-testid="download-codes-btn"
                >
                  <Download className="w-4 h-4" /> Download Codes
                </button>
                <button
                  onClick={() => setShowCodesModal(false)}
                  className="w-full px-5 py-3 rounded-xl font-bold text-[#64748b] text-sm hover:bg-[#f1f5f9] transition-all"
                  data-testid="close-codes-modal"
                >
                  I've saved my codes — continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationSuccessPage;
